import logoMobile from "../assets/brand/logomobile.svg";
import globe from "../assets/brand/globe.svg";
import FooterColumn from "./common/FooterColumn";
import Button from "./ui/Button";
import Input from "./ui/Input";
import SocialIconButton from "./ui/SocialIconButton";
import LanguageToggle from "./common/LanguageToggle";

const FOOTER_COLUMNS = [
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Careers", to: "/careers" },
      // { label: "Volunteer", to: "/volunteers" },
      { label: "Contact Us", to: "/contact" },
      { label: "Blogs", to: "/blog" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Service", to: "/terms-of-service" },
      { label: "Cookie Policy", to: "/cookie-policy" },
    ],
  },
  {
    title: "Technologies",
    links: [
      { label: "Web Development", to: "/services#web-mobile" },
      { label: "App Development", to: "/services#web-mobile" },
      { label: "Custom Software", to: "/services#custom-software" },
      { label: "Process Automation", to: "/services#process-automation" },
    ],
  },
  {
    title: "Academy",
    links: [
      {
        label: "AI Course - Prompt engineering",
        to: "/contact?subject=Book%20a%20Consultation",
      },
      {
        label: "Cyber Security Course",
        to: "/contact?subject=Book%20a%20Consultation",
      },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "FAQs", to: "/contact" },
      { label: "Help Center", to: "/contact" },
      { label: "Contact us", to: "/contact" },
    ],
  },
  {
    title: "Explore",
    links: [
      { label: "Bao Africa", to: "/about#services" },
      { label: "BA Port", to: "/about" },
      { label: "Services", to: "/services" },
      { label: "Government Solutions", to: "/government-solutions" },
    ],
  },
  {
    title: "Documentation",
    links: [{ label: "Design System", to: "/contact" }],
  },
] as const;

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-[#154181] text-white">
      {/* Background Decorative Elements */}
      <div
        className="absolute w-[1000px] h-[766px] left-[630px] top-[-352px] bg-white/5 pointer-events-none"
        style={{ borderRadius: "50%", filter: "blur(120px)" }}
      />
      <div
        className="absolute w-[90px] h-[142px] left-[1114px] top-[25px] bg-white/5 pointer-events-none"
        style={{
          borderRadius: "40px",
          transform: "rotate(-15deg)",
          filter: "blur(40px)",
        }}
      />
      <div
        className="absolute w-[89px] h-[143px] left-[1303px] top-[-40px] bg-white/5 pointer-events-none"
        style={{
          borderRadius: "40px",
          transform: "rotate(15deg)",
          filter: "blur(40px)",
        }}
      />

      {/* Subtle Logo Texture */}
      <img
        src={logoMobile}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-200px] w-[600px] -translate-x-1/2 opacity-[0.03] invert brightness-0"
      />

      <div className="relative z-10 mx-auto max-w-[1360px] pb-12 pt-10 max-[768px]:px-4 max-[768px]:pt-8">
        <section className="flex items-start justify-between gap-8 max-[1100px]:flex-col">
          <div className="max-w-[520px] max-[700px]:max-w-full max-[700px]:text-center">
            <h2 className="font-title text-[56px] font-medium leading-[1.05] text-white max-[768px]:text-[42px] max-[700px]:text-[28px]">
              Subscribe to our
              <br />
              newsletter
            </h2>
            <p className="mt-3 max-w-[460px] font-description text-[16px] font-light leading-[1.6] text-[#9CC0F5] max-[700px]:mx-auto max-[700px]:text-[14px]">
              Stay informed with Bao Technologies — hear about product updates,
              upcoming events, and expert insights delivered monthly. Join our
              mailing list for curated articles, case studies, and special
              offers tailored to your interests.
            </p>
          </div>

          <div className="ml-auto mt-32 w-full max-w-[570px] max-[1100px]:ml-0 max-[1100px]:mt-2">
            <div className="flex items-center gap-3 max-[700px]:flex-col max-[700px]:items-stretch">
              <Input
                type="email"
                placeholder="Enter your email"
                className="h-[36px] w-full rounded-[12px] border border-[#9CC0F5] bg-transparent px-4 font-description text-[16px] font-light text-white outline-none placeholder:text-[#45699C]"
              />
              <Button className="h-[32px] whitespace-nowrap rounded-[12px] border border-white/15 bg-[rgba(255,255,255,0.30)] px-8 font-description text-[16px] font-medium text-[#154181] max-[700px]:w-full">
                Subscribe
              </Button>
            </div>

            <label className="mt-3 flex items-center gap-2 font-description text-[14px] font-light text-[#9CC0F5]">
              <Input
                type="checkbox"
                className="h-4 w-4 appearance-none rounded-[4px] border border-[#9CC0F5] bg-transparent checked:bg-transparent checked:before:relative checked:before:left-[4px] checked:before:top-[1px] checked:before:block checked:before:h-[9px] checked:before:w-[5px] checked:before:rotate-45 checked:before:border-b-2 checked:before:border-r-2 checked:before:border-[#9CC0F5] checked:before:content-['']"
              />
              <span>
                I agree to receive occasional emails with news, product updates,
                and events from Bao Technologies.
              </span>
            </label>
          </div>
        </section>

        <div className="my-14 h-px bg-[#395E95]" />

        <section
          className="grid grid-cols-[repeat(6,max-content)] justify-start gap-x-[60px] gap-y-8 max-[1100px]:grid-cols-3 max-[700px]:grid-cols-1 max-[700px]:gap-y-6"
          aria-label="Footer links"
        >
          {FOOTER_COLUMNS.map((column) => (
            <FooterColumn
              key={column.title}
              title={column.title}
              links={column.links}
            />
          ))}
        </section>

        <div className="mb-3 mt-20 h-px bg-[#395E95]" />

        <div className="flex items-center justify-between gap-4 max-[700px]:flex-col max-[700px]:items-start">
          <div className="inline-flex flex-col items-start gap-4 max-[980px]:w-full">
            <div className="flex w-full items-center justify-between gap-4">
              <LanguageToggle
                className="inline-flex items-center gap-[4px] border-0 bg-transparent p-0 font-title text-[14px] font-extralight text-white"
                globeSrc={globe}
                fullLabel="English (UK)"
                shortLabel=""
                globeClassName="h-[12px] w-[12px] brightness-0 invert"
                fullLabelClassName=""
                shortLabelClassName="hidden"
                chevronClassName="ml-[-2px] h-[13px] w-[13px]"
              />
              <p className="hidden text-right font-description text-[14px] font-extralight text-white max-[980px]:block max-[700px]:hidden">
                © All rights reserved - Bao Technologies 2026
              </p>
            </div>

            <div className="inline-flex items-center gap-[10px] max-[980px]:hidden">
              <SocialIconButton
                aria-label="Instagram"
                href="https://www.instagram.com/baotechnologies?igsh=ZHRkNHlzZng5emZv&utm_source=qr"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-[14px] w-[14px]"
                  aria-hidden="true"
                >
                  <path
                    d="M8 3H16C19.314 3 22 5.686 22 9V15C22 18.314 19.314 21 16 21H8C4.686 21 2 18.314 2 15V9C2 5.686 4.686 3 8 3Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 16.2C14.319 16.2 16.2 14.319 16.2 12C16.2 9.681 14.319 7.8 12 7.8C9.681 7.8 7.8 9.681 7.8 12C7.8 14.319 9.681 16.2 12 16.2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M17.5 6.7H17.51"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </SocialIconButton>

              <SocialIconButton
                aria-label="Facebook"
                href="https://www.facebook.com/profile.php?id=61570610012334"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-[14px] w-[14px]"
                  aria-hidden="true"
                >
                  <path
                    d="M14 8H16V5H14C11.791 5 10 6.791 10 9V11H8V14H10V21H13V14H15.2L16 11H13V9C13 8.448 13.448 8 14 8Z"
                    fill="currentColor"
                  />
                </svg>
              </SocialIconButton>

              <SocialIconButton
                aria-label="LinkedIn"
                href="https://www.linkedin.com/company/bao-technologies-sarl"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-[14px] w-[14px]"
                  aria-hidden="true"
                >
                  <path d="M6.5 9.5H4V20H6.5V9.5Z" fill="currentColor" />
                  <path
                    d="M5.25 4C4.56 4 4 4.56 4 5.25C4 5.94 4.56 6.5 5.25 6.5C5.94 6.5 6.5 5.94 6.5 5.25C6.5 4.56 5.94 4 5.25 4Z"
                    fill="currentColor"
                  />
                  <path
                    d="M20 13.6C20 10.98 18.64 9.3 16.15 9.3C14.93 9.3 14.18 9.97 13.85 10.45V9.5H11.4V20H13.85V14.9C13.85 13.56 14.11 12.26 15.77 12.26C17.41 12.26 17.43 13.79 17.43 14.98V20H19.9L20 13.6Z"
                    fill="currentColor"
                  />
                </svg>
              </SocialIconButton>
            </div>
          </div>

          <p className="flex-1 text-center font-description text-[14px] font-extralight text-white max-[980px]:hidden max-[700px]:w-full max-[700px]:text-left">
            © All rights reserved - Bao Technologies 2026
          </p>

          <p className="hidden w-full font-description text-[14px] font-extralight text-white max-[700px]:block">
            © All rights reserved - Bao Technologies 2026
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
